import { useMemo } from 'react';
import {
  CustomFieldDefinition,
  CustomFieldValueInput,
  CustomFieldType,
} from '@/graphql/generatedTypes';
import { Field, FieldLabel, FieldSet } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  buildCustomFieldValuesMap,
  isCustomFieldVisible,
} from './customFieldUtils';
import i18n from '@/utils/i18n';

interface AssetCustomFieldsProps {
  definitions: CustomFieldDefinition[];
  values: CustomFieldValueInput[];
  onChange: (values: CustomFieldValueInput[]) => void;
  disabled?: boolean;
}

/**
 * Orders fields so that fields with depends_on appear right after their dependency.
 * Uses topological sort to handle dependency chains.
 */
function orderFields(
  definitions: CustomFieldDefinition[],
): CustomFieldDefinition[] {
  const result: CustomFieldDefinition[] = [];
  const remaining = [...definitions];

  // Safety limit to prevent infinite loops
  let iterations = 0;
  const maxIterations = remaining.length * remaining.length;

  while (remaining.length > 0 && iterations < maxIterations) {
    iterations++;

    // Find a field that has no dependency, or whose dependency is already in result
    const nextIndex = remaining.findIndex(
      (f) =>
        !f.depends_on || result.some((r) => r.id === f.depends_on?.field_id),
    );

    if (nextIndex === -1) {
      // No valid field found (circular dependency or missing dependency)
      // Just add remaining fields as-is
      result.push(...remaining);
      break;
    }

    result.push(remaining[nextIndex]);
    remaining.splice(nextIndex, 1);
  }

  return result;
}

export function AssetCustomFields({
  definitions,
  values,
  onChange,
  disabled = false,
}: AssetCustomFieldsProps) {
  // Order fields with dependents after their dependencies
  const orderedFields = useMemo(() => orderFields(definitions), [definitions]);

  // Build values map including defaults for visibility checks
  const valuesMap = useMemo(
    () => buildCustomFieldValuesMap(definitions, values),
    [definitions, values],
  );

  // Get value for a field, falling back to default_value from definition
  const getValue = (fieldId: string): string | undefined => {
    const existing = values.find((v) => v.id === fieldId);
    if (existing) return existing.value ?? undefined;

    const def = definitions.find((d) => d.id === fieldId);
    return def?.default_value ?? undefined;
  };

  // Update a single field value
  const updateValue = (fieldId: string, newValue: string | undefined) => {
    const existingIndex = values.findIndex((v) => v.id === fieldId);

    let updatedValues: CustomFieldValueInput[];
    if (existingIndex >= 0) {
      updatedValues = values.map((v, i) =>
        i === existingIndex ? { id: fieldId, value: newValue } : v,
      );
    } else {
      updatedValues = [...values, { id: fieldId, value: newValue }];
    }

    onChange(updatedValues);
  };

  // Filter to only visible fields
  const visibleFields = orderedFields.filter((f) =>
    isCustomFieldVisible(f, valuesMap),
  );

  if (visibleFields.length === 0) {
    return null;
  }

  return (
    <>
      {visibleFields.map((field) => {
        const value = getValue(field.id);
        const isRequired = field.required ?? false;

        return (
          <FieldSet key={field.id}>
            <Field>
              <FieldLabel>
                {field.name}
                {isRequired && <span className="tw:text-destructive">*</span>}
              </FieldLabel>
              {renderFieldInput(field, value, updateValue, disabled)}
            </Field>
          </FieldSet>
        );
      })}
    </>
  );
}

function renderFieldInput(
  field: CustomFieldDefinition,
  value: string | undefined,
  updateValue: (fieldId: string, value: string | undefined) => void,
  disabled: boolean,
) {
  switch (field.type) {
    case CustomFieldType.Boolean:
      return (
        <Select
          value={value ?? '__none__'}
          onValueChange={(val) =>
            updateValue(field.id, val === '__none__' ? undefined : val)
          }
          disabled={disabled}
        >
          <SelectTrigger className="tw:w-full">
            <SelectValue placeholder={i18n.t('Common:select')} />
          </SelectTrigger>
          <SelectContent>
            {!field.required && <SelectItem value="__none__">—</SelectItem>}
            <SelectItem value="true">{i18n.t('Common:yes')}</SelectItem>
            <SelectItem value="false">{i18n.t('Common:no')}</SelectItem>
          </SelectContent>
        </Select>
      );

    case CustomFieldType.Integer:
      return (
        <Input
          type="number"
          step="1"
          min="0"
          value={value ?? ''}
          onChange={(e) => updateValue(field.id, e.target.value || undefined)}
          disabled={disabled}
          placeholder={field.default_value ?? ''}
        />
      );

    case CustomFieldType.Number:
      return (
        <Input
          type="text"
          inputMode="decimal"
          value={value ?? ''}
          onChange={(e) => {
            // Allow digits, comma, and dot
            const sanitized = e.target.value.replace(/[^\d.,]/g, '');
            updateValue(field.id, sanitized || undefined);
          }}
          disabled={disabled}
          placeholder={field.default_value ?? ''}
        />
      );

    case CustomFieldType.String:
    default:
      return (
        <Input
          type="text"
          value={value ?? ''}
          onChange={(e) => updateValue(field.id, e.target.value || undefined)}
          disabled={disabled}
          placeholder={field.default_value ?? ''}
        />
      );
  }
}
